import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import ReactMarkdown from 'react-markdown';
import SEO from './SEO';

const ArticleDetail = ({ isDarkMode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const docRef = doc(db, 'articles', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setArticle({ id: docSnap.id, ...docSnap.data() });
        } else {
          alert('სტატია ვერ მოიძებნა!');
          navigate('/');
        }
      } catch (error) {
        console.error("შეცდომა სტატიის ჩატვირთვისას:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-55 text-gray-900'
      }`}>
        <p className="text-center animate-pulse text-xl">იტვირთება...</p>
      </div>
    );
  }

  if (!article) return null;

  const formattedDate = article.createdAt?.seconds
    ? new Date(article.createdAt.seconds * 1000).toLocaleDateString('ka-GE', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })
    : 'ახლახან';

  return (
    <div className={`min-h-screen pb-12 transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-55 text-gray-900'
    }`}>
      
      {/* SEO მეტა-ტეგები */}
      <SEO 
        title={article.title}
        description={article.summary?.substring(0, 160)}
        path={`/article/${id}`}
        image={article.imageUrl}
        type="article"
      />

      {/* Schema.org JSON-LD (Google Rich Results-ისთვის) */}
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "${article.title.replace(/"/g, '\\"')}",
              "description": "${article.summary?.substring(0, 160).replace(/"/g, '\\"')}",
              "image": "${article.imageUrl}",
              "datePublished": "${article.createdAt?.seconds ? new Date(article.createdAt.seconds * 1000).toISOString() : new Date().toISOString()}",
              "author": {
                "@type": "Organization",
                "name": "New Innovation"
              }
            }
          `}
        </script>
      </Helmet>

      <nav className={`py-4 px-6 shadow-sm transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate('/')} className="text-blue-500 hover:text-blue-600 font-bold transition-colors">
            ← მთავარ გვერდზე დაბრუნება
          </button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto mt-10 px-4">
        <div className="flex items-center gap-4 mb-4">
          <span className={`text-xs font-bold uppercase tracking-wide px-3 py-1 rounded ${
            isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
          }`}>
            {article.category}
          </span>
          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            🕒 {formattedDate}
          </span>
        </div>
        
        <h1 className="text-3xl md:text-5xl font-extrabold mb-6 leading-tight">
          {article.title}
        </h1>
        
        {article.imageUrl && (
          <img src={article.imageUrl} alt={article.title} className="w-full h-[400px] object-cover rounded-2xl shadow-md mb-8" />
        )}
        
        <div className={`text-lg leading-relaxed space-y-4 prose max-w-none ${
          isDarkMode ? 'prose-invert text-gray-300' : 'text-gray-700'
        }`}>
          <ReactMarkdown>{article.content}</ReactMarkdown>
        </div>
      </main>
    </div>
  );
};

export default ArticleDetail;